/**
 * Admin view — Hub Administrator panel.
 *
 * Operational layout (8 sections in order):
 *   1. Статистика по всем магазинам за сегодня (агрегат)
 *   2. Карточки магазинов (per-shop stats + supervisorName)
 *   3. Все диалоги за сегодня (read-only)
 *   4. Магазины: онлайн-сотрудники и нагрузка
 *   5. Отложенные и не взяты
 *   6. SLA / просрочки
 *   7. Каналы связи
 *   8. Система и Интеграции
 *
 * NO operator elements: no shift controls, no assign/answer/escalate/close buttons.
 * Admin is NOT in the routing queue as first receiver.
 * Сотрудники (Section D) moved to lower reference block — not in top operational area.
 *
 * Data source: fetchAdminDashboard() → /api/v1/admin/dashboard
 * No localStorage / cookies / sessionStorage writes.
 */

import {
  Activity,
  AlertCircle,
  BarChart3,
  Building2,
  CheckCircle2,
  Clock,
  Database,
  MessageSquare,
  Plug,
  Server,
  ShieldAlert,
  UsersRound,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchAdminDashboard } from '../api/hubApi';
import type {
  AdminChannel,
  AdminChannelStatus,
  AdminDashboard,
  AdminDialogEntry,
  AdminIntegration,
  AdminShop,
  AdminShopStats,
  AdminStaffMember,
  AdminStats,
  AdminSystemStatus,
  IntegrationState,
  SystemComponentStatus,
} from '../api/types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CLASS: Record<SystemComponentStatus, string> = {
  ok: 'done',
  warning: 'next',
  error: 'failed',
  unknown: 'skip',
};

const STATUS_LABEL: Record<SystemComponentStatus, string> = {
  ok: 'OK',
  warning: 'Предупреждение',
  error: 'Ошибка',
  unknown: 'Нет данных',
};

const CHANNEL_STATUS_CLASS: Record<AdminChannelStatus, string> = {
  active: 'done',
  next_stage: 'next',
  not_connected: 'skip',
};

const CHANNEL_STATUS_LABEL: Record<AdminChannelStatus, string> = {
  active: 'Активен',
  next_stage: 'Следующий этап',
  not_connected: 'Не подключён',
};

const INTEGRATION_CLASS: Record<IntegrationState, string> = {
  ready: 'done',
  mock: 'next',
  planned: 'skip',
  down: 'failed',
};

const INTEGRATION_LABEL: Record<IntegrationState, string> = {
  ready: 'Готово',
  mock: 'Mock',
  planned: 'Запланировано',
  down: 'Недоступно',
};

function StatusDot({ status }: { status: SystemComponentStatus }) {
  const icons: Record<SystemComponentStatus, JSX.Element> = {
    ok: <CheckCircle2 size={14} className="ok-icon" />,
    warning: <AlertCircle size={14} style={{ color: 'var(--color-warning, #f59e0b)' }} />,
    error: <XCircle size={14} style={{ color: 'var(--color-error, #ef4444)' }} />,
    unknown: <Clock size={14} style={{ opacity: 0.5 }} />,
  };
  return icons[status] ?? null;
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}с`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}м ${seconds % 60}с`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}ч ${m}м`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Section 1: Aggregate Stats (all shops today) ────────────────────────────

function SectionAggregateStats({ stats }: { stats: AdminStats }) {
  return (
    <section className="panel" aria-labelledby="admin-agg-stats-title" data-testid="admin-section-aggregate-stats">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 1 · Все магазины</p>
          <h2 id="admin-agg-stats-title">Статистика по всем магазинам за сегодня</h2>
        </div>
        <BarChart3 size={20} />
      </div>
      <div style={{ padding: '0 24px 16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <StatCard label="Открытые диалоги сейчас" value={stats.openNow} testId="admin-stats-openNow" />
          <StatCard label="Всего диалогов за сегодня" value={stats.totalToday} testId="admin-stats-totalToday" />
          <StatCard label="Диалоги не взяты в работу" value={stats.notTaken} testId="admin-stats-notTaken" />
          <StatCard label="Диалоги в работе" value={stats.inWork} testId="admin-stats-inWork" />
          <StatCard label="Диалоги закрыты сегодня" value={stats.closedToday} testId="admin-stats-closedToday" />
          <StatCard label="Диалоги эскалированы" value={stats.escalated} testId="admin-stats-escalated" />
          <StatCard label="Отложены" value={stats.deferred ?? 0} testId="admin-stats-deferred" />
        </div>
        {/* SLA block */}
        <div style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '8px' }}>SLA / просрочки</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            maxWidth: 420,
          }}
        >
          <StatCard label="SLA в норме" value={stats.slaOk} accent="ok" testId="admin-stats-slaOk" />
          <StatCard label="Предупреждение" value={stats.slaWarning} accent="warning" testId="admin-stats-slaWarning" />
          <StatCard label="SLA нарушен" value={stats.slaBreached} accent="error" testId="admin-stats-slaBreached" />
        </div>
        <p style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: 12 }}>
          Рассчитано: {formatTime(stats.calculatedAt)}
        </p>
      </div>
    </section>
  );
}

function StatCard({
  label,
  value,
  accent,
  testId,
}: {
  label: string;
  value: number;
  accent?: 'ok' | 'warning' | 'error';
  testId?: string;
}) {
  const accentColor =
    accent === 'ok'
      ? 'var(--color-success, #22c55e)'
      : accent === 'warning'
      ? 'var(--color-warning, #f59e0b)'
      : accent === 'error'
      ? 'var(--color-error, #ef4444)'
      : undefined;

  return (
    <div
      data-testid={testId}
      style={{
        background: 'rgba(128,128,128,0.07)',
        borderRadius: 8,
        padding: '10px 12px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: '1.6rem',
          fontWeight: 700,
          color: accentColor,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ─── Section 2: Per-shop cards ────────────────────────────────────────────────

function SectionShopCards({ perShopStats, shops }: { perShopStats?: AdminShopStats[]; shops: AdminShop[] }) {
  // Build from perShopStats if available, otherwise fall back to shops data
  const cards: AdminShopStats[] = perShopStats && perShopStats.length > 0
    ? perShopStats
    : shops.map((s) => ({
        shopId: s.id,
        shopName: s.name,
        shopCode: s.code,
        crmId: s.crmId,
        supervisorName: s.supervisorName ?? null,
        openNow: s.openNow ?? 0,
        totalToday: s.totalToday ?? 0,
        notTakenToday: 0,
        onlineStaffCount: s.onlineStaffCount ?? 0,
      }));

  return (
    <section className="panel" aria-labelledby="admin-shop-cards-title" data-testid="admin-section-shop-cards">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 2 · Карточки магазинов</p>
          <h2 id="admin-shop-cards-title">Статистика по каждому магазину за сегодня</h2>
        </div>
        <Building2 size={20} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '12px',
          padding: '0 24px 16px',
        }}
      >
        {cards.map((s) => (
          <div
            key={s.shopId}
            data-testid={`admin-shop-card-${s.shopId}`}
            style={{
              background: 'rgba(128,128,128,0.06)',
              borderRadius: 10,
              padding: '14px 16px',
              border: '1px solid rgba(128,128,128,0.12)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.shopName}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.55, marginTop: 2 }}>
                  <span style={{ fontFamily: 'monospace' }}>{s.shopCode}</span>
                  {' · '}CRM ID {s.crmId}
                </div>
              </div>
              {s.supervisorName && (
                <div
                  data-testid={`admin-shop-supervisor-${s.shopId}`}
                  style={{
                    fontSize: '0.7rem',
                    opacity: 0.7,
                    textAlign: 'right',
                    maxWidth: 120,
                    lineHeight: 1.3,
                  }}
                >
                  {s.supervisorName}
                </div>
              )}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '6px',
                fontSize: '0.82rem',
              }}
            >
              <div style={{ background: 'rgba(128,128,128,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{s.openNow}</div>
                <div style={{ fontSize: '0.68rem', opacity: 0.6, marginTop: 2 }}>Открытые диалоги сейчас</div>
              </div>
              <div style={{ background: 'rgba(128,128,128,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{s.totalToday}</div>
                <div style={{ fontSize: '0.68rem', opacity: 0.6, marginTop: 2 }}>Всего диалогов за сегодня</div>
              </div>
              <div style={{ background: 'rgba(128,128,128,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{s.notTakenToday}</div>
                <div style={{ fontSize: '0.68rem', opacity: 0.6, marginTop: 2 }}>Диалоги не взяты в работу</div>
              </div>
              <div style={{ background: 'rgba(128,128,128,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{s.onlineStaffCount}</div>
                <div style={{ fontSize: '0.68rem', opacity: 0.6, marginTop: 2 }}>Онлайн-сотрудники</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Section 3: All dialogs today (read-only) ─────────────────────────────────

function SectionTodayDialogs({ dialogs, todayDialogs }: { dialogs: AdminDialogEntry[]; todayDialogs?: AdminDialogEntry[] }) {
  // Prefer todayDialogs if available; fallback to filtering dialogs client-side
  const today = new Date().toDateString();
  const displayDialogs = todayDialogs
    ? todayDialogs
    : dialogs.filter((d) => {
        try {
          return new Date(d.createdAt).toDateString() === today;
        } catch {
          return false;
        }
      });

  if (displayDialogs.length === 0) {
    return (
      <section className="panel" aria-labelledby="admin-today-dialogs-title" data-testid="admin-section-today-dialogs">
        <div className="panel-header">
          <div>
            <p className="eyebrow">Раздел 3 · только просмотр</p>
            <h2 id="admin-today-dialogs-title">Все диалоги за сегодня</h2>
          </div>
          <MessageSquare size={20} />
        </div>
        <div className="empty-state" data-testid="admin-today-dialogs-empty" style={{ padding: '24px' }}>
          <p>Диалогов за сегодня нет</p>
        </div>
      </section>
    );
  }

  return (
    <section className="panel" aria-labelledby="admin-today-dialogs-title" data-testid="admin-section-today-dialogs">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 3 · только просмотр · {displayDialogs.length} диалогов</p>
          <h2 id="admin-today-dialogs-title">Все диалоги за сегодня</h2>
        </div>
        <MessageSquare size={20} />
      </div>
      <div style={{ overflowX: 'auto', padding: '0 24px 16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.2)', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Время</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Клиент</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Телефон</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Магазин</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Канал</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Статус</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Ответственный</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>CRM расч.</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>SLA</th>
            </tr>
          </thead>
          <tbody>
            {displayDialogs.map((d) => (
              <tr
                key={d.id}
                data-testid={`admin-today-dialog-${d.id}`}
                style={{ borderBottom: '1px solid rgba(128,128,128,0.08)' }}
              >
                <td style={{ padding: '6px 8px', fontSize: '0.75rem' }}>
                  {formatTime(d.createdAt)}
                </td>
                <td style={{ padding: '6px 8px' }}>{d.clientName}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  {d.phone}
                </td>
                <td style={{ padding: '6px 8px' }}>{d.shopName}</td>
                <td style={{ padding: '6px 8px' }}>
                  <span className={`channel-badge ${d.channel}`}>{d.channel}</span>
                </td>
                <td style={{ padding: '6px 8px' }}>{d.statusLabel || d.status}</td>
                <td style={{ padding: '6px 8px' }}>{d.assignedOperatorName || '—'}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                  {d.crmCalculationId ?? '—'}
                </td>
                <td style={{ padding: '6px 8px' }}>
                  {d.slaStatus ? (
                    <span className={`sla-timer sla-${d.slaStatus}`}>{d.slaStatus}</span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Section 4: Shops — online staff and load ─────────────────────────────────

function SectionShopsOnlineStaff({ shops }: { shops: AdminShop[] }) {
  return (
    <section className="panel" aria-labelledby="admin-shops-online-title" data-testid="admin-section-shops">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 4 · Магазины</p>
          <h2 id="admin-shops-online-title">Магазины: онлайн-сотрудники и нагрузка</h2>
        </div>
        <Building2 size={20} />
      </div>
      <div style={{ overflowX: 'auto', padding: '0 24px 16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.2)', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Название</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Код</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>CRM ID</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Статус</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Онлайн-сотрудники</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }} data-testid="admin-shops-col-openNow">Открытые диалоги сейчас</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }} data-testid="admin-shops-col-totalToday">Всего диалогов за сегодня</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Руководитель</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((s) => (
              <tr
                key={s.id}
                data-testid={`admin-shop-${s.id}`}
                style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}
              >
                <td style={{ padding: '6px 8px' }}>{s.name}</td>
                <td style={{ padding: '6px 8px' }}>
                  <span className="badge" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {s.code}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{s.crmId}</td>
                <td style={{ padding: '6px 8px' }}>
                  <span className={`availability-badge ${s.active ? 'available' : 'off'}`}>
                    {s.active ? 'Активен' : 'Выключен'}
                  </span>
                </td>
                <td style={{ padding: '6px 8px' }}>{s.onlineStaffCount ?? 0}</td>
                <td style={{ padding: '6px 8px' }}>{s.openNow != null ? s.openNow : '—'}</td>
                <td style={{ padding: '6px 8px' }}>{s.totalToday != null ? s.totalToday : '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  {s.supervisorName
                    ? <span data-testid={`admin-shop-supervisor-row-${s.id}`}>{s.supervisorName}</span>
                    : <span style={{ opacity: 0.4 }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Section 5: Deferred and not-taken dialogs ────────────────────────────────

function SectionDeferredNotTaken({ stats, todayDialogs }: { stats: AdminStats; todayDialogs: AdminDialogEntry[] }) {
  const notTakenDialogs = todayDialogs.filter(
    (d) => d.status === 'queued' || d.status === 'new' || d.status === 'bot'
  );

  return (
    <section className="panel" aria-labelledby="admin-deferred-title" data-testid="admin-section-deferred">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 5</p>
          <h2 id="admin-deferred-title">Отложенные и не взяты в работу</h2>
        </div>
        <Clock size={20} />
      </div>
      <div style={{ padding: '0 24px 16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '12px',
            marginBottom: notTakenDialogs.length > 0 ? '16px' : 0,
          }}
        >
          <StatCard
            label="Диалоги не взяты в работу"
            value={stats.notTaken}
            testId="admin-deferred-notTaken"
          />
          <StatCard
            label="Отложены"
            value={stats.deferred ?? 0}
            testId="admin-deferred-deferred"
          />
        </div>
        {notTakenDialogs.length > 0 && (
          <div>
            <p style={{ fontSize: '0.78rem', opacity: 0.6, marginBottom: 8 }}>
              Диалоги без оператора ({notTakenDialogs.length}):
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {notTakenDialogs.map((d) => (
                <div
                  key={d.id}
                  data-testid={`admin-not-taken-${d.id}`}
                  style={{
                    display: 'flex',
                    gap: 12,
                    fontSize: '0.82rem',
                    padding: '6px 10px',
                    background: 'rgba(128,128,128,0.06)',
                    borderRadius: 6,
                  }}
                >
                  <span style={{ opacity: 0.55 }}>{formatTime(d.createdAt)}</span>
                  <span>{d.clientName}</span>
                  <span style={{ opacity: 0.7 }}>{d.shopName}</span>
                  <span>
                    <span className={`channel-badge ${d.channel}`}>{d.channel}</span>
                  </span>
                  <span style={{ opacity: 0.7 }}>{d.statusLabel || d.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Section 6: SLA / overdue ─────────────────────────────────────────────────

function SectionSla({ stats }: { stats: AdminStats }) {
  return (
    <section className="panel" aria-labelledby="admin-sla-title" data-testid="admin-section-sla">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 6 · SLA</p>
          <h2 id="admin-sla-title">SLA / просрочки</h2>
        </div>
        <AlertCircle size={20} />
      </div>
      <div style={{ padding: '0 24px 16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '12px',
          }}
        >
          <StatCard label="SLA в норме" value={stats.slaOk} accent="ok" testId="admin-sla-ok" />
          <StatCard label="Предупреждение SLA" value={stats.slaWarning} accent="warning" testId="admin-sla-warning" />
          <StatCard label="SLA нарушен" value={stats.slaBreached} accent="error" testId="admin-sla-breached" />
          <StatCard label="Эскалированы" value={stats.escalated} testId="admin-sla-escalated" />
        </div>
      </div>
    </section>
  );
}

// ─── Section 7: Channels ──────────────────────────────────────────────────────

function SectionChannels({ channels }: { channels: AdminChannel[] }) {
  return (
    <section className="panel" aria-labelledby="admin-channels-title" data-testid="admin-section-channels">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 7 · Каналы связи</p>
          <h2 id="admin-channels-title">Каналы</h2>
        </div>
        <Plug size={20} />
      </div>
      <ol className="timeline" style={{ padding: '0 24px 16px' }}>
        {channels.map((ch) => (
          <li
            key={ch.code}
            className={CHANNEL_STATUS_CLASS[ch.status]}
            data-testid={`admin-channel-${ch.code}`}
          >
            <span className="timeline-dot" />
            <div>
              <strong>
                {ch.name} · {CHANNEL_STATUS_LABEL[ch.status]}
              </strong>
              <p>Режим: {ch.mode} · {ch.comment}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ─── Section 8: System ────────────────────────────────────────────────────────

function SectionSystem({ system }: { system: AdminSystemStatus }) {
  return (
    <section className="panel" aria-labelledby="admin-system-title" data-testid="admin-section-system">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 8А · Система</p>
          <h2 id="admin-system-title">Система</h2>
        </div>
        <Server size={20} />
      </div>

      <dl className="details-list" style={{ padding: '0 24px 16px' }}>
        {/* Backend */}
        <div data-testid="admin-system-backend">
          <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusDot status={system.backend.status} />
            Backend
          </dt>
          <dd>
            {STATUS_LABEL[system.backend.status]} · {system.backend.nodeEnv} ·
            порт {system.backend.port} · uptime {formatUptime(system.backend.uptime)}
          </dd>
        </div>

        {/* Database */}
        <div data-testid="admin-system-database">
          <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusDot status={system.database.status} />
            База данных
          </dt>
          <dd>{system.database.note}</dd>
        </div>

        {/* CRM */}
        <div data-testid="admin-system-crm">
          <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusDot status={system.crm.readiness === 'ready' ? 'ok' : system.crm.readiness === 'blocked' ? 'error' : 'warning'} />
            CRM Grace
          </dt>
          <dd>
            Режим: <strong>{system.crm.mode}</strong> · readiness: {system.crm.readiness} ·
            URL: {system.crm.apiUrl} · логин: {system.crm.serviceLogin} ·
            пароль: {system.crm.servicePassword}
          </dd>
        </div>

        {/* Telegram */}
        <div data-testid="admin-system-telegram">
          <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusDot status={system.telegram.botTokenSet ? 'ok' : 'warning'} />
            Telegram
          </dt>
          <dd>
            Режим: <strong>{system.telegram.mode}</strong> ·
            токен: {system.telegram.botTokenSet ? 'задан' : 'не задан'} ·
            webhook secret: {system.telegram.webhookSecretSet ? 'задан' : 'не задан'} ·
            public URL: {system.telegram.publicUrlSet ? 'задан' : 'не задан'} ·
            {system.telegram.pollingOrWebhook}
          </dd>
        </div>
        {system.telegram.note && (
          <div>
            <dt>Подсказка</dt>
            <dd>{system.telegram.note}</dd>
          </div>
        )}

        {/* Last check */}
        <div data-testid="admin-system-checked">
          <dt>Последняя проверка</dt>
          <dd>{formatTime(system.checkedAt)}</dd>
        </div>
      </dl>
    </section>
  );
}

// ─── Section 8B: Integrations ─────────────────────────────────────────────────

function SectionIntegrations({ integrations }: { integrations: AdminIntegration[] }) {
  return (
    <section className="panel" aria-labelledby="admin-integrations-title" data-testid="admin-section-integrations">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Раздел 8Б · Интеграции</p>
          <h2 id="admin-integrations-title">Интеграции</h2>
        </div>
        <Database size={20} />
      </div>
      <ol className="timeline" style={{ padding: '0 24px 16px' }}>
        {integrations.map((i) => (
          <li
            key={i.code}
            className={INTEGRATION_CLASS[i.state]}
            data-testid={`admin-integration-${i.code}`}
          >
            <span className="timeline-dot" />
            <div>
              <strong>
                {i.title} · {INTEGRATION_LABEL[i.state]}
              </strong>
              <p>{i.note}</p>
              {i.lastCheckedAt && (
                <p style={{ fontSize: '0.75rem', opacity: 0.5 }}>
                  Проверено: {formatTime(i.lastCheckedAt)}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ─── Reference block: Staff (lower / non-operational) ────────────────────────

// ─── Staff role keywords for client-side display grouping ────────────────────
// The server already filters to shop-facing roles only.
// ADMIN_STAFF_VISIBLE_ROLES is kept as a client-side defense-in-depth guard:
// if the API ever returns unexpected roles, visibleStaff filters them out.
// Shop-facing role codes and keywords accepted in reference staff block.
// Server already filters; this Set is used for client-side visibleStaff guard.
// Order: shop_manager / shop_supervisor first, then aliases.
const ADMIN_STAFF_VISIBLE_ROLES = new Set([
  'shop_manager',
  'shop_supervisor',
  'manager',
  'supervisor',
  'менеджер',
  'оператор',
  'operator',
  'руководитель',
  'директор',
]);

const STAFF_EMPTY_LABEL = 'Нет сотрудников с ролями менеджер / руководитель магазина';

/**
 * Returns true if the role code/string is a shop-facing role that should
 * be displayed in the reference staff section. Server already filters, but
 * this is a defensive client-side check.
 */
function isAdminVisibleRole(role: string): boolean {
  if (!role) return false;
  const lower = role.toLowerCase();
  // Check exact set membership first (fast path)
  if (ADMIN_STAFF_VISIBLE_ROLES.has(lower)) return true;
  // Check substring match against the same keywords
  for (const keyword of ADMIN_STAFF_VISIBLE_ROLES) {
    if (lower.includes(keyword)) return true;
  }
  return false;
}

interface SectionStaffProps {
  staff: AdminStaffMember[];
  staffSource?: string;
  staffError?: string;
}

function SectionStaff({ staff, staffSource, staffError }: SectionStaffProps) {
  // API already filters to visible roles. visibleStaff is a defense-in-depth
  // client-side guard against any role that slips through from the server.
  const visibleStaff = staff.filter((u) => isAdminVisibleRole(u.role));
  const isError = staffSource === 'error';
  const isEmpty = visibleStaff.length === 0;

  return (
    <section className="panel" aria-labelledby="admin-staff-title" data-testid="admin-section-staff">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Справочник · Сотрудники (CRM live)</p>
          <h2 id="admin-staff-title">Все сотрудники</h2>
        </div>
        <UsersRound size={20} />
      </div>

      {isError && (
        <div
          data-testid="admin-staff-crm-error"
          style={{
            margin: '0 24px 12px',
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.08)',
            borderRadius: 8,
            fontSize: '0.82rem',
            color: 'var(--color-error, #ef4444)',
          }}
        >
          <strong>CRM недоступна:</strong> {staffError ?? 'не удалось загрузить сотрудников'}.
          {' '}Mock-данные не показываются.
        </div>
      )}

      {isEmpty && !isError && (
        <div
          className="empty-state"
          data-testid="admin-staff-empty"
          style={{ padding: '24px' }}
        >
          <p>{STAFF_EMPTY_LABEL}</p>
          {staffSource === 'empty' && (
            <p style={{ fontSize: '0.78rem', opacity: 0.6, marginTop: 6 }}>
              CRM вернула 0 пользователей с подходящими ролями.
              Проверьте роли сотрудников в CRM Grace.
            </p>
          )}
        </div>
      )}

      {!isEmpty && (
        <div style={{ overflowX: 'auto', padding: '0 24px 16px' }}>
          <p style={{ fontSize: '0.75rem', opacity: 0.55, marginBottom: 8 }}>
            Источник: CRM Grace /admin-users · {visibleStaff.length} сотр.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.2)', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Имя / логин</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Роль</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Магазин(ы)</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Активен</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Смена</th>
              </tr>
            </thead>
            <tbody>
              {visibleStaff.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`admin-staff-${u.id}`}
                  style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}
                >
                  <td style={{ padding: '6px 8px' }}>
                    <div>{u.name}</div>
                    {u.firedDate && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-error,#ef4444)' }}>
                        Уволен: {u.firedDate}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{u.roleTitle}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {u.shopsString ?? u.shopName ?? '—'}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <span className={`availability-badge ${u.active ? 'available' : 'off'}`}>
                      {u.active ? 'Да' : 'Нет'}
                    </span>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <span className={`availability-badge ${u.shiftStatus ?? 'off'}`}>
                      {u.shiftLabel ?? (
                        u.shiftStatus === 'available' ? 'На смене'
                        : u.shiftStatus === 'break' ? 'Перерыв'
                        : 'Не на смене'
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── Main AdminView ───────────────────────────────────────────────────────────

interface AdminApiResponse {
  data: AdminDashboard;
  _staffSource?: string;
  _staffError?: string;
}

export function AdminView() {
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);
  const [staffSource, setStaffSource] = useState<string | undefined>(undefined);
  const [staffError, setStaffError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAdminDashboard()
      .then((res) => {
        if (cancelled) return;
        setDashboard(res.data);
        // Extract staff source metadata from the envelope
        const envelope = res as unknown as AdminApiResponse;
        setStaffSource(envelope._staffSource);
        setStaffError(envelope._staffError);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="panel" data-testid="admin-loading">
        <p style={{ padding: 24 }}>Загружаем панель администратора…</p>
      </section>
    );
  }

  if (error || !dashboard) {
    return (
      <section className="panel" data-testid="admin-error">
        <div style={{ padding: 24 }}>
          <ShieldAlert size={24} style={{ marginBottom: 8, color: 'var(--color-error, #ef4444)' }} />
          <p>Ошибка загрузки данных: {error ?? 'нет данных'}</p>
        </div>
      </section>
    );
  }

  // Build today-only dialogs for sections that need them
  const today = new Date().toDateString();
  const todayDialogs: AdminDialogEntry[] = dashboard.todayDialogs
    ? dashboard.todayDialogs
    : dashboard.dialogs.filter((d) => {
        try { return new Date(d.createdAt).toDateString() === today; } catch { return false; }
      });

  return (
    <>
      {/* Header — admin-only, no operator controls */}
      <header className="topbar" data-testid="admin-topbar">
        <div>
          <p className="eyebrow">Панель администратора хаба</p>
          <h1>Администратор</h1>
          <p className="topbar-subtitle">
            Оперативный обзор: Статистика · Магазины · Диалоги сегодня · SLA · Каналы · Система.
            Только просмотр.
          </p>
        </div>
        <Activity size={22} style={{ opacity: 0.6 }} />
      </header>

      {/* ── OPERATIONAL BLOCKS ── */}

      {/* Section 1 — Aggregate stats all shops today */}
      <SectionAggregateStats stats={dashboard.stats} />

      {/* Section 2 — Per-shop cards */}
      <SectionShopCards perShopStats={dashboard.perShopStats} shops={dashboard.shops} />

      {/* Section 3 — All dialogs today (read-only) */}
      <SectionTodayDialogs dialogs={dashboard.dialogs} todayDialogs={dashboard.todayDialogs} />

      {/* Section 4 — Shops: online staff and load */}
      <SectionShopsOnlineStaff shops={dashboard.shops} />

      {/* Section 5 — Deferred and not-taken */}
      <SectionDeferredNotTaken stats={dashboard.stats} todayDialogs={todayDialogs} />

      {/* Section 6 — SLA / overdue */}
      <SectionSla stats={dashboard.stats} />

      {/* ── TECHNICAL / REFERENCE BLOCKS ── */}

      {/* Section 7 — Channels */}
      <SectionChannels channels={dashboard.channels} />

      {/* Section 8A + 8B — System and Integrations */}
      <div className="main-grid">
        <SectionSystem system={dashboard.system} />
        <SectionIntegrations integrations={dashboard.integrations} />
      </div>

      {/* Reference: Staff — hidden per product decision (2025-01, ticket: hide admin staff block) */}
      {/* <SectionStaff
        staff={dashboard.staff}
        staffSource={staffSource}
        staffError={staffError}
      /> */}
    </>
  );
}
